import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  ChefHat, LayoutDashboard, BookOpen, UtensilsCrossed, Package, Truck,
  Users, BarChart2, TrendingUp, Globe, Settings, Menu, X, LogOut,
  ClipboardList, Plug, Crown, Share2, Coins, HelpCircle,
  DollarSign, Eye, Star, Zap, Clock, CheckCircle2, AlertTriangle,
  ShoppingCart, Sparkles, ChevronRight, Play, Bell, ShieldAlert,
} from "lucide-react";

// ── DEMO DATA ─────────────────────────────────────────────────────────────────
const RESTAURANT = { name: "La Brasserie Urbana", cuisine: "Cocina fusion contemporanea", plan: "Pro", members: 6 };
const STATS = { revenue: 48750, orders: 1842, avgTicket: 26.47, customers: 634, monthGrowth: 12.3, topDish: "Wagyu Burger Trufa", creditUsed: 234, creditTotal: 400 };
const MENUS = [
  { id: "m1", name: "Carta Principal", dishes: 32, categories: 6, active: true },
  { id: "m2", name: "Menu del Dia", dishes: 8, categories: 3, active: true, price: 16.90 },
  { id: "m3", name: "Carta de Cocteles", dishes: 18, categories: 4, active: true },
];
const DISHES = [
  { id: "d1", name: "Wagyu Burger Trufa", price: 22.50, category: "Principales", allergens: ["gluten", "huevo", "leche"], sales: 342, margin: 68 },
  { id: "d2", name: "Ceviche Nikkei", price: 16.00, category: "Entrantes", allergens: ["pescado", "soja", "sesamo"], sales: 289, margin: 72 },
  { id: "d3", name: "Risotto de Setas y Parmesano", price: 18.50, category: "Principales", allergens: ["leche", "gluten"], sales: 256, margin: 65 },
  { id: "d4", name: "Tataki de Atun Rojo", price: 19.00, category: "Entrantes", allergens: ["pescado", "soja", "sesamo"], sales: 198, margin: 71 },
  { id: "d5", name: "Tarta Tatin de Manzana", price: 9.50, category: "Postres", allergens: ["gluten", "huevo", "leche"], sales: 187, margin: 78 },
  { id: "d6", name: "Pulpo a la Brasa", price: 21.00, category: "Principales", allergens: ["moluscos"], sales: 176, margin: 62 },
  { id: "d7", name: "Ensalada Caesar con Pollo", price: 13.50, category: "Entrantes", allergens: ["huevo", "leche", "gluten"], sales: 312, margin: 74 },
  { id: "d8", name: "Cheesecake Yuzu", price: 8.50, category: "Postres", allergens: ["leche", "huevo", "gluten"], sales: 145, margin: 80 },
];
const CUSTOMERS_TOP = [
  { name: "Maria Garcia", visits: 34, spent: 892, loyalty: "Gold" },
  { name: "Carlos Rodriguez", visits: 28, spent: 756, loyalty: "Gold" },
  { name: "Ana Martinez", visits: 22, spent: 612, loyalty: "Silver" },
  { name: "Pedro Lopez", visits: 19, spent: 534, loyalty: "Silver" },
  { name: "Laura Sanchez", visits: 15, spent: 423, loyalty: "Bronce" },
];
const WEEKLY_SALES = [
  { day: "Lun", amount: 1850 }, { day: "Mar", amount: 2100 }, { day: "Mie", amount: 1920 },
  { day: "Jue", amount: 2350 }, { day: "Vie", amount: 3200 }, { day: "Sab", amount: 4100 }, { day: "Dom", amount: 3650 },
];
const ALLERGEN_COLORS = {
  gluten: "bg-amber-500/15 text-amber-700", huevo: "bg-yellow-500/15 text-yellow-600",
  leche: "bg-blue-500/15 text-blue-600", pescado: "bg-sky-500/15 text-sky-600",
  soja: "bg-lime-500/15 text-lime-600", sesamo: "bg-stone-500/15 text-stone-600",
  moluscos: "bg-indigo-500/15 text-indigo-600", crustaceos: "bg-red-500/15 text-red-700",
};

const SECTIONS = [
  { id: "dashboard",  icon: LayoutDashboard, label: "Dashboard" },
  { id: "menus",      icon: BookOpen,        label: "Menus" },
  { id: "recipes",    icon: UtensilsCrossed, label: "Recetas" },
  { id: "inventory",  icon: Package,         label: "Inventario" },
  { id: "suppliers",  icon: Truck,           label: "Proveedores" },
  { id: "shopping",   icon: ClipboardList,   label: "Listas de Compra" },
  { id: "customers",  icon: Users,           label: "Clientes" },
  { id: "analytics",  icon: BarChart2,       label: "Analisis" },
  { id: "forecast",   icon: TrendingUp,      label: "Previsiones IA" },
  { id: "tpv",        icon: Plug,            label: "Integracion TPV" },
  { id: "website",    icon: Globe,           label: "Web" },
  { id: "social",     icon: Share2,          label: "Redes Sociales" },
  { id: "team",       icon: Users,           label: "Equipo" },
  { id: "settings",   icon: Settings,        label: "Configuracion" },
  { id: "subscription", icon: Coins,         label: "Suscripcion" },
  { id: "help",       icon: HelpCircle,      label: "Ayuda" },
];

// Sections with full demo content
const ACTIVE_SECTIONS = ["dashboard", "menus", "recipes", "allergens", "customers", "website", "analytics"];

const TOUR_STEPS = [
  { section: "dashboard", title: "Panel de Control", desc: "Vision completa de ventas, pedidos y metricas clave en tiempo real. Controla tu negocio de un vistazo." },
  { section: "menus", title: "Gestion de Menus", desc: "Crea y edita cartas, menus del dia y cartas de bebidas. Con categorias, precios y estado de publicacion." },
  { section: "recipes", title: "Recetas y Costes", desc: "Cada plato con sus ingredientes, coste por racion y margen de beneficio. Optimiza tu rentabilidad." },
  { section: "allergens", title: "Alergenos con IA", desc: "Deteccion automatica de alergenos usando inteligencia artificial. Cumplimiento EU con un click." },
  { section: "customers", title: "Fidelizacion", desc: "Programa de puntos, niveles de fidelidad y descuentos por cumpleanos automaticos." },
  { section: "website", title: "Web del Restaurante", desc: "Genera tu pagina web publica con 7 temas visuales, menu interactivo y QR de acceso." },
  { section: "analytics", title: "Analisis Avanzado", desc: "Tendencias de ventas, productos estrella, horas pico y previsiones IA para compras inteligentes." },
];

// ── MINI BAR CHART ────────────────────────────────────────────────────────────
function MiniBar({ data, colorClass = "bg-primary", height = "h-28" }) {
  const max = Math.max(...data.map(d => d.amount));
  return (
    <div className={`flex items-end gap-1.5 ${height}`}>
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className={`w-full rounded-md ${colorClass} transition-all duration-500`}
            style={{ height: `${(d.amount / max) * 100}%`, minHeight: 4 }}
          />
          <span className="text-[10px] text-muted-foreground">{d.day}</span>
        </div>
      ))}
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, colorClass = "text-primary bg-primary/10" }) {
  const [textColor, bgColor] = colorClass.split(" ");
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex-1 min-w-[150px]">
      <div className="flex items-center gap-2 mb-2.5">
        <div className={`rounded-lg p-1.5 ${bgColor}`}>
          <Icon className={`w-4 h-4 ${textColor}`} />
        </div>
        <span className="text-xs text-muted-foreground font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold text-foreground">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

// ── Placeholder section for nav items without full content ─────────────────────
function PlaceholderSection({ section }) {
  const s = SECTIONS.find(x => x.id === section);
  const Icon = s?.icon || LayoutDashboard;
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid={`demo-${section}`}>
      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-primary" />
      </div>
      <h2 className="text-lg font-semibold mb-2">{s?.label || section}</h2>
      <p className="text-sm text-muted-foreground max-w-md">
        Este modulo esta disponible en la version completa de MenuGenius. Solicita acceso para explorar todas las funcionalidades.
      </p>
      <Button className="mt-4" size="sm" onClick={() => window.open("/", "_blank")} data-testid={`demo-cta-${section}`}>
        Solicitar acceso
      </Button>
    </div>
  );
}

// ── MAIN DEMO PAGE ────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DemoPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("dashboard");
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [demoAllowed, setDemoAllowed] = useState(null);

  // Check if demo is enabled
  useEffect(() => {
    fetch(`${API_URL}/api/site-settings`).then(r => r.json())
      .then(d => { if (d.demo_enabled === false) navigate("/", { replace: true }); else setDemoAllowed(true); })
      .catch(() => setDemoAllowed(true));
  }, [navigate]);

  useEffect(() => {
    if (tourActive && TOUR_STEPS[tourStep]) {
      setActiveSection(TOUR_STEPS[tourStep].section);
    }
  }, [tourActive, tourStep]);

  const nextTour = () => {
    if (tourStep < TOUR_STEPS.length - 1) setTourStep(s => s + 1);
    else { setTourActive(false); setTourStep(0); }
  };

  const currentTour = tourActive ? TOUR_STEPS[tourStep] : null;
  const activeLabel = SECTIONS.find(s => s.id === activeSection)?.label || "Dashboard";
  const creditPct = Math.round((STATS.creditUsed / STATS.creditTotal) * 100);

  // Insert allergens section at position 3 (after recipes) for sidebar display
  const sidebarSections = [...SECTIONS];
  const recipesIdx = sidebarSections.findIndex(s => s.id === "recipes");
  if (recipesIdx >= 0 && !sidebarSections.find(s => s.id === "allergens")) {
    sidebarSections.splice(recipesIdx + 1, 0, { id: "allergens", icon: ShieldAlert, label: "Alergenos" });
  }

  if (!demoAllowed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex font-sans text-foreground">

      {/* ── SIDEBAR (matches DashboardLayout exactly) ── */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col h-full">

          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading text-lg font-semibold">MenuGenius</span>
            </div>
            <button className="lg:hidden p-2 hover:bg-muted rounded-lg" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Restaurant selector (mock) */}
          <div className="p-4 border-b border-border">
            <div className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="demo-restaurant-selector">
              <div className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                <ChefHat className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="truncate flex-1 font-medium">{RESTAURANT.name}</span>
            </div>
            {/* Role badge */}
            <div className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium w-fit text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400">
              <Crown className="w-3 h-3" />
              Propietario
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-3">
              {sidebarSections.map((item) => {
                const isActive = activeSection === item.id;
                const isTourHighlight = currentTour?.section === item.id;
                const hasContent = ACTIVE_SECTIONS.includes(item.id);
                return (
                  <Button
                    key={item.id}
                    variant={isActive ? "secondary" : "ghost"}
                    className={`w-full justify-start gap-3 ${
                      isActive
                        ? "bg-primary/10 text-primary hover:bg-primary/15"
                        : "text-muted-foreground hover:text-foreground"
                    } ${isTourHighlight && !isActive ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : ""}`}
                    onClick={() => { setActiveSection(item.id); if (tourActive) setTourActive(false); setSidebarOpen(false); }}
                    data-testid={`demo-nav-${item.id}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {!hasContent && <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />}
                  </Button>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Tour toggle */}
          <div className="px-3 pb-2">
            <Button
              variant={tourActive ? "default" : "outline"}
              className={`w-full gap-2 ${!tourActive ? "border-primary/40 text-primary hover:bg-primary/10" : ""}`}
              onClick={() => { setTourActive(!tourActive); setTourStep(0); }}
              data-testid="demo-tour-toggle"
            >
              {tourActive ? <X className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {tourActive ? "Salir del tour" : "Tour guiado"}
            </Button>
          </div>

          {/* Mock user section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 px-2">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">DU</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">Demo User</p>
                <p className="text-xs text-muted-foreground truncate">demo@menugenius.online</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 text-muted-foreground hover:text-foreground gap-2 justify-start"
              onClick={() => navigate("/")}
              data-testid="demo-back"
            >
              <LogOut className="w-4 h-4" />
              Volver a la web
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ── MAIN CONTENT AREA ── */}
      <div className="flex-1 lg:ml-64">

        {/* Top bar (matches DashboardLayout exactly) */}
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-30">
          <div className="h-full flex items-center justify-between px-4 lg:px-8">
            <button className="lg:hidden p-2 hover:bg-muted rounded-lg" onClick={() => setSidebarOpen(true)} data-testid="demo-mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1 lg:flex-none flex items-center gap-2">
              <h1 className="font-heading text-base lg:text-lg font-semibold truncate">{activeLabel}</h1>
              <Badge variant="outline" className="text-[10px] font-semibold text-primary border-primary/30 hidden sm:inline-flex">DEMO</Badge>
            </div>
            <div className="flex items-center gap-3">
              {/* Credit widget */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60" data-testid="demo-credit-widget">
                <Coins className="w-3.5 h-3.5 text-primary" />
                <div className="flex items-center gap-1.5">
                  <div className="w-16 h-1.5 bg-muted-foreground/20 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${creditPct >= 90 ? "bg-red-500" : creditPct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${creditPct}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground font-medium">{STATS.creditTotal - STATS.creditUsed}</span>
                </div>
              </div>
              <span className="text-sm text-muted-foreground hidden sm:block">{RESTAURANT.name}</span>
              {/* Mock notification bell */}
              <div className="relative w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors cursor-default" data-testid="demo-notification-bell">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">3</span>
              </div>
            </div>
          </div>
        </header>

        {/* Tour overlay */}
        {currentTour && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-card border border-primary/30 rounded-2xl p-5 max-w-md w-[90%] shadow-2xl shadow-primary/10" data-testid="demo-tour-overlay">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[10px] text-primary font-bold uppercase tracking-wider">
                Paso {tourStep + 1} de {TOUR_STEPS.length}
              </span>
              <button onClick={() => setTourActive(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <h3 className="text-base font-bold mb-1.5">{currentTour.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">{currentTour.desc}</p>
            <Button onClick={nextTour} className="w-full gap-1" data-testid="demo-tour-next">
              {tourStep < TOUR_STEPS.length - 1 ? "Siguiente" : "Finalizar tour"}
              <ChevronRight className="w-4 h-4" />
            </Button>
            {/* Progress dots */}
            <div className="flex gap-1 justify-center mt-3">
              {TOUR_STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === tourStep ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"}`} />
              ))}
            </div>
          </div>
        )}

        {/* ── PAGE CONTENT ── */}
        <main className="p-4 lg:p-8">

          {/* ── DASHBOARD ── */}
          {activeSection === "dashboard" && (
            <div data-testid="demo-dashboard">
              <h1 className="text-xl font-bold mb-1">Buenos dias, Chef</h1>
              <p className="text-sm text-muted-foreground mb-6">{RESTAURANT.name} — Resumen del mes</p>
              <div className="flex gap-3 flex-wrap mb-6">
                <StatCard icon={DollarSign} label="Facturacion" value={`${STATS.revenue.toLocaleString("es")} EUR`} sub={`+${STATS.monthGrowth}% vs mes anterior`} />
                <StatCard icon={ShoppingCart} label="Pedidos" value={STATS.orders.toLocaleString()} sub={`Ticket medio: ${STATS.avgTicket} EUR`} colorClass="text-blue-500 bg-blue-500/10" />
                <StatCard icon={Users} label="Clientes unicos" value={STATS.customers.toString()} sub="634 registrados" colorClass="text-violet-500 bg-violet-500/10" />
                <StatCard icon={Zap} label="Creditos IA" value={`${STATS.creditUsed}/${STATS.creditTotal}`} sub={`${creditPct}% utilizado`} colorClass="text-amber-500 bg-amber-500/10" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold mb-4">Ventas de la semana</h3>
                  <MiniBar data={WEEKLY_SALES} />
                </div>
                <div className="bg-card border border-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold mb-3">Top platos del mes</h3>
                  {DISHES.slice(0, 5).map((d, i) => (
                    <div key={d.id} className={`flex items-center gap-2.5 py-2 ${i < 4 ? "border-b border-border" : ""}`}>
                      <span className="text-xs font-bold text-primary w-5">#{i + 1}</span>
                      <span className="flex-1 text-sm font-medium">{d.name}</span>
                      <span className="text-xs text-muted-foreground">{d.sales} uds</span>
                      <span className={`text-xs font-semibold ${d.margin >= 70 ? "text-emerald-500" : d.margin >= 60 ? "text-amber-500" : "text-red-500"}`}>{d.margin}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── MENUS ── */}
          {activeSection === "menus" && (
            <div data-testid="demo-menus">
              <h1 className="text-xl font-bold mb-5">Gestion de Menus</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {MENUS.map(m => (
                  <div key={m.id} className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-semibold">{m.name}</h3>
                      <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/30 bg-emerald-500/10">Activo</Badge>
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span>{m.dishes} platos</span>
                      <span>{m.categories} categorias</span>
                      {m.price && <span className="text-primary font-semibold">{m.price} EUR</span>}
                    </div>
                  </div>
                ))}
              </div>
              <h2 className="text-base font-semibold mb-4">Platos destacados</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {DISHES.map(d => (
                  <div key={d.id} className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors">
                    <div className="flex justify-between items-start gap-2">
                      <h4 className="text-sm font-semibold flex-1">{d.name}</h4>
                      <span className="text-sm font-bold text-primary flex-shrink-0">{d.price.toFixed(2)}&euro;</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 mb-2">{d.category}</p>
                    <div className="flex gap-1 flex-wrap">
                      {(Array.isArray(d.allergens) ? d.allergens : []).map(a => (
                        <span key={a} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${ALLERGEN_COLORS[a] || "bg-muted text-muted-foreground"} capitalize`}>{a}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── RECIPES ── */}
          {activeSection === "recipes" && (
            <div data-testid="demo-recipes">
              <h1 className="text-xl font-bold mb-5">Recetas y Escandallos</h1>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] px-5 py-3 bg-muted text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  <span>Plato</span><span>Coste</span><span>Precio</span><span>Margen</span><span>Ventas</span>
                </div>
                {DISHES.map((d, i) => (
                  <div key={d.id} className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr] px-5 py-3.5 items-center ${i < DISHES.length - 1 ? "border-b border-border" : ""}`}>
                    <span className="text-sm font-medium">{d.name}</span>
                    <span className="text-sm text-muted-foreground">{(d.price * (1 - d.margin / 100)).toFixed(2)} EUR</span>
                    <span className="text-sm">{d.price.toFixed(2)} EUR</span>
                    <span className={`text-sm font-semibold ${d.margin >= 70 ? "text-emerald-500" : d.margin >= 60 ? "text-amber-500" : "text-red-500"}`}>{d.margin}%</span>
                    <span className="text-sm text-muted-foreground">{d.sales}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ALLERGENS ── */}
          {activeSection === "allergens" && (
            <div data-testid="demo-allergens">
              <div className="flex justify-between items-center mb-5">
                <h1 className="text-xl font-bold">Gestion de Alergenos</h1>
                <Button className="gap-2" size="sm" data-testid="demo-allergens-ai-btn">
                  <Sparkles className="w-4 h-4" /> Analizar todos con IA
                </Button>
              </div>
              <div className="bg-muted/60 border border-border rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>8/8 platos analizados — 2 creditos IA por plato</span>
                <Badge variant="outline" className="ml-auto text-[10px] text-emerald-500 border-emerald-500/30 bg-emerald-500/10">100% analizado</Badge>
              </div>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                {DISHES.map((d, i) => (
                  <div key={d.id} className={`flex items-center gap-3 px-5 py-3.5 ${i < DISHES.length - 1 ? "border-b border-border" : ""}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="flex-1 text-sm font-medium">{d.name}</span>
                    <div className="flex gap-1 flex-wrap">
                      {(Array.isArray(d.allergens) ? d.allergens : []).map(a => (
                        <span key={a} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ALLERGEN_COLORS[a] || "bg-muted text-muted-foreground"} capitalize`}>{a}</span>
                      ))}
                    </div>
                    <span className="text-[10px] text-emerald-500 font-semibold ml-2">95%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── CUSTOMERS ── */}
          {activeSection === "customers" && (
            <div data-testid="demo-customers">
              <h1 className="text-xl font-bold mb-5">Programa de Fidelizacion</h1>
              <div className="flex gap-3 flex-wrap mb-6">
                <StatCard icon={Users} label="Clientes registrados" value="634" colorClass="text-violet-500 bg-violet-500/10" />
                <StatCard icon={Star} label="Miembros Gold" value="12" sub="Top 2% de clientes" colorClass="text-amber-500 bg-amber-500/10" />
                <StatCard icon={DollarSign} label="Gasto medio/cliente" value="42.30 EUR" colorClass="text-emerald-500 bg-emerald-500/10" />
              </div>
              <h2 className="text-base font-semibold mb-3">Top clientes</h2>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                {CUSTOMERS_TOP.map((c, i) => (
                  <div key={i} className={`flex items-center gap-3 px-5 py-3.5 ${i < CUSTOMERS_TOP.length - 1 ? "border-b border-border" : ""}`}>
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{c.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-[11px] text-muted-foreground">{c.visits} visitas</p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] font-semibold ${
                      c.loyalty === "Gold" ? "text-amber-500 border-amber-500/30 bg-amber-500/10" :
                      c.loyalty === "Silver" ? "text-muted-foreground border-border bg-muted/50" :
                      "text-orange-700 border-orange-700/30 bg-orange-700/10"
                    }`}>{c.loyalty}</Badge>
                    <span className="text-sm font-semibold">{c.spent.toFixed(2)} EUR</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── WEBSITE ── */}
          {activeSection === "website" && (
            <div data-testid="demo-website">
              <h1 className="text-xl font-bold mb-5">Web del Restaurante</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <StatCard icon={Globe} label="Visitas este mes" value="2,847" sub="+18% vs anterior" colorClass="text-blue-500 bg-blue-500/10" />
                <StatCard icon={Eye} label="Menu visto" value="1,923" sub="67% de visitantes" colorClass="text-violet-500 bg-violet-500/10" />
              </div>
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-sm font-semibold mb-4">7 temas disponibles</h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
                  {[
                    { name: "Mediterraneo", bg: "bg-[#FDFCF8]", accent: "bg-[#E05D3A]", textDark: true },
                    { name: "Moderno", bg: "bg-[#0F0F0F]", accent: "bg-[#FF6B35]", textDark: false },
                    { name: "Elegante", bg: "bg-[#F8F5F0]", accent: "bg-[#8B6914]", textDark: true },
                    { name: "Minimalista", bg: "bg-card", accent: "bg-[#2D2D2D]", textDark: true },
                    { name: "Rustico", bg: "bg-[#F5EBD8]", accent: "bg-[#8B4513]", textDark: true },
                    { name: "Casual", bg: "bg-[#1A1A1A]", accent: "bg-[#C8A97E]", textDark: false },
                    { name: "Casual Fast", bg: "bg-[#1C1C1E]", accent: "bg-primary", textDark: false, active: true },
                  ].map(t => (
                    <div key={t.name} className={`${t.bg} rounded-lg p-3 text-center ${t.active ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "border border-border"}`}>
                      <div className={`w-5 h-5 rounded-full ${t.accent} mx-auto mb-2`} />
                      <span className={`text-[10px] font-semibold ${t.textDark ? "text-foreground" : "text-gray-200"}`}>{t.name}</span>
                      {t.active && <p className="text-[8px] text-primary font-bold mt-1">ACTIVO</p>}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-2.5 items-center">
                  <div className="flex-1 bg-muted rounded-lg px-3.5 py-2.5 text-xs">
                    <span className="text-muted-foreground">URL publica:</span>
                    <span className="text-primary ml-1.5 font-medium">menugenius.online/r/la-brasserie</span>
                  </div>
                  <Button size="sm" data-testid="demo-website-preview-btn">Ver web</Button>
                </div>
              </div>
            </div>
          )}

          {/* ── ANALYTICS ── */}
          {activeSection === "analytics" && (
            <div data-testid="demo-analytics">
              <h1 className="text-xl font-bold mb-5">Analisis y Tendencias</h1>
              <div className="flex gap-3 flex-wrap mb-6">
                <StatCard icon={TrendingUp} label="Crecimiento mensual" value="+12.3%" sub="Tendencia positiva sostenida" colorClass="text-emerald-500 bg-emerald-500/10" />
                <StatCard icon={Clock} label="Hora pico" value="14:00 - 15:00" sub="Sabados" colorClass="text-amber-500 bg-amber-500/10" />
                <StatCard icon={Star} label="Plato estrella" value="Wagyu Burger" sub="342 ventas este mes" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold mb-4">Distribucion por categoria</h3>
                  {[
                    { cat: "Principales", pct: 45, color: "bg-primary" },
                    { cat: "Entrantes", pct: 30, color: "bg-blue-500" },
                    { cat: "Postres", pct: 15, color: "bg-violet-500" },
                    { cat: "Bebidas", pct: 10, color: "bg-emerald-500" },
                  ].map(c => (
                    <div key={c.cat} className="mb-2.5">
                      <div className="flex justify-between text-xs mb-1">
                        <span>{c.cat}</span><span className="font-semibold">{c.pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted">
                        <div className={`h-full rounded-full ${c.color} transition-all duration-500`} style={{ width: `${c.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-card border border-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold mb-4">Prevision IA proxima semana</h3>
                  <MiniBar data={[
                    { day: "Lun", amount: 1900 }, { day: "Mar", amount: 2200 }, { day: "Mie", amount: 2000 },
                    { day: "Jue", amount: 2500 }, { day: "Vie", amount: 3400 }, { day: "Sab", amount: 4300 }, { day: "Dom", amount: 3800 },
                  ]} colorClass="bg-violet-500" />
                  <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-violet-500" /> Prevision generada por IA con datos historicos
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── PLACEHOLDER SECTIONS ── */}
          {!ACTIVE_SECTIONS.includes(activeSection) && <PlaceholderSection section={activeSection} />}
        </main>
      </div>
    </div>
  );
}
